/**
 * FollowListModal — Shows followers or following list in a modal.
 * Fetches from /api/social/followers or /api/social/following.
 */
import { useState, useEffect, useCallback } from 'react';
import { X, UserPlus, UserCheck, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FollowListModal = ({ isOpen, onClose, tab = 'followers', userId }) => {
  const [activeTab, setActiveTab] = useState(tab);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followingIds, setFollowingIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [fersRes, fingRes, idsRes] = await Promise.all([
        fetch(`${API_URL}/api/social/followers`, { credentials: 'include' }),
        fetch(`${API_URL}/api/social/following`, { credentials: 'include' }),
        fetch(`${API_URL}/api/social/following-ids`, { credentials: 'include' }),
      ]);
      if (fersRes.ok) { const d = await fersRes.json(); setFollowers(d.followers || []); }
      if (fingRes.ok) { const d = await fingRes.json(); setFollowing(d.following || []); }
      if (idsRes.ok) { const d = await idsRes.json(); setFollowingIds(new Set(d.following_ids || [])); }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isOpen) fetchData(); }, [isOpen, fetchData]);
  useEffect(() => { setActiveTab(tab); }, [tab]);

  const handleFollow = async (targetId) => {
    setActionLoading(targetId);
    try {
      const isFollowing = followingIds.has(targetId);
      const res = await fetch(`${API_URL}/api/social/${isFollowing ? 'unfollow' : 'follow'}/${targetId}`, {
        method: isFollowing ? 'DELETE' : 'POST', credentials: 'include',
      });
      if (res.ok) {
        setFollowingIds(prev => {
          const next = new Set(prev);
          isFollowing ? next.delete(targetId) : next.add(targetId);
          return next;
        });
      }
    } catch {} finally { setActionLoading(null); }
  };

  const handleUserClick = (uid) => { onClose(); navigate(`/profile/${uid}`); };

  if (!isOpen) return null;

  const list = activeTab === 'followers' ? followers : following;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="follow-list-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md mx-4 max-h-[70vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {['followers', 'following'].map(t => (
              <button key={t} onClick={() => setActiveTab(t)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`tab-${t}`}>
                {t === 'followers' ? `Followers (${followers.length})` : `Following (${following.length})`}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" data-testid="close-follow-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-gray-400 animate-spin" /></div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3"><UserPlus className="w-7 h-7 text-gray-300" /></div>
              <p className="text-sm font-semibold text-gray-900">{activeTab === 'followers' ? 'No followers yet' : 'Not following anyone yet'}</p>
              <p className="text-xs text-gray-500 mt-1">{activeTab === 'followers' ? 'Share your food posts to gain followers!' : 'Discover and follow other foodies'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {list.map(u => {
                const uid = u.user_id;
                const isMe = uid === userId;
                const amFollowing = followingIds.has(uid);
                return (
                  <div key={uid} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors" data-testid={`follow-user-${uid}`}>
                    <button onClick={() => handleUserClick(uid)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-11 h-11 rounded-full object-cover ring-1 ring-gray-100" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F4C430] flex items-center justify-center text-white font-bold text-sm">
                          {(u.name || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{u.name || 'Foodie'}</p>
                        {u.username && <p className="text-xs text-gray-500 truncate">@{u.username}</p>}
                      </div>
                    </button>
                    {!isMe && (
                      <button onClick={() => handleFollow(uid)} disabled={actionLoading === uid}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all flex-shrink-0 ${
                          amFollowing
                            ? 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500 border border-gray-200'
                            : 'bg-[#FF6B35] text-white hover:bg-[#E5592E] shadow-sm'
                        }`} data-testid={`follow-action-${uid}`}>
                        {actionLoading === uid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : amFollowing ? <><UserCheck className="w-3.5 h-3.5" />Following</> : <><UserPlus className="w-3.5 h-3.5" />Follow</>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowListModal;
